<template>
  <section class="testimonials-section">
    <div class="container">
      <h2 class="section-title">O que dizem meus pacientes</h2>
      
      <div v-if="loading" class="loading-state">Carregando depoimentos...</div>

      <div v-else class="testimonials-grid">
        <div v-for="item in testimonials" :key="item.id" class="testimonial-wrapper">
          <div class="testimonial-card">
            <div class="quote-mark">“ ”</div>

            <p class="testimonial-text" v-html="item.text"></p>

            <div class="stars">
              <span v-for="n in item.rating" :key="n">★</span>
            </div>
          </div>

          <div class="client-info">
            <div class="client-photo">
              <img :src="item.imageUrl" :alt="item.name">
            </div>
            <h3 class="client-name">{{ item.name }}</h3>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { toRaw } from 'vue';

const HYGRAPH_ENDPOINT = process.env.VUE_APP_HYGRAPH_URL;



const testimonials = ref([]);
const loading = ref(true);

const fetchTestimonials = async () => {
  const query = `
    query GetDepoimentos {
      testimonials {
        id
        patientName
        text
        rating
        image { url }
      }
    }
  `;

  try {
    const response = await fetch(HYGRAPH_ENDPOINT!, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query })
    });
    
    const { data } = await response.json();
    //console.log(JSON.stringify(data,null,2));
    if (data && data.testimonials) {
      testimonials.value = data.testimonials.map((item: any) => ({
        id: item.id,
        name: item.patientName, 
        text: item.text,
        rating: item.rating || 5,
        imageUrl: item.image?.url || 'https://via.placeholder.com/100'
      }));
    }   
  } catch (error) {
    console.error("Erro ao buscar depoimentos:", error);
  } finally {
    loading.value = false;
  }
};

onMounted(fetchTestimonials);
</script>

<style scoped>
  .testimonials-section {
    background-color: #9ACB56; /* Verde da referência */
    padding: 80px 40px;
    border-radius: 40px;
    margin: 40px auto;
    max-width: 1200px;
    text-align: center;
  }

  .section-title {
    color: white;
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 60px;
  }

  .testimonials-grid {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 40px;
  }

  .quote-mark {
    font-size: 5rem;
    line-height: 1;
    color: #9ACB56;
    opacity: 0.3;
    font-family: serif;
    margin-bottom: -20px;
  }

  .client-photo {
    width: 85px;
    height: 85px;
    border-radius: 50%;
    border: 4px solid #9ACB56; /* Borda da cor do fundo para o efeito de recorte */
    overflow: hidden;
    background: white;
    margin-bottom: 10px;
    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  }

  .client-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .client-name {
    color: white;
    font-size: 1.1rem;
    font-weight: 700;
  }

  .loading-state {
    color: white;
    font-weight: bold;
  }

  @media (max-width: 768px) {
    .testimonial-wrapper {
      max-width: 100%;
    }
  }

  /* Container que envolve o card e os elementos que vazam */
  .testimonial-wrapper {
    flex: 1;
    min-width: 450px; /* Garante que não fiquem muito estreitos */
    max-width: 450px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  /* O CARD BRANCO PADRONIZADO */
  .testimonial-card {
    background: white;
    border-radius: 30px;
    padding: 40px 30px 80px 30px; /* Padding extra no fundo para a foto */
    
    /* ESTES SÃO OS AJUSTES DE PADRONIZAÇÃO */
    width: 100%;
    min-width: 450px;
    min-height: 450px; /* Força todos os cards a terem o mesmo tamanho mínimo */
    display: flex;
    flex-direction: column;
    justify-content: space-between; /* Distribui as aspas, texto e estrelas igualmente */
    
    box-shadow: 0 10px 20px rgba(0,0,0,0.05);
    position: relative;
  }

  /* Ajuste do texto para ocupar o espaço central */
  .testimonial-text {
    flex-grow: 1; /* Faz o texto ocupar o espaço disponível, empurrando as estrelas para baixo */
    display: flex;
    align-items: center; /* Centraliza o texto verticalmente se for curto */
    justify-content: center;
    text-align: center;
    font-size: 1.1rem;
    line-height: 1.6;
    color: #555;
    font-style: italic;
    margin: 20px 0;
  }

  /* Estrelas sempre alinhadas no fundo do card branco */
  .stars {
    color: #FFD700;
    font-size: 1.2rem;
    text-align: center;
    margin-top: auto;
  }

  /* Foto e Nome (Fora do card branco) */
  .client-info {
    margin-top: -50px; /* Faz a foto "vazar" para dentro do card */
    z-index: 5;
    text-align: center;
  }

  /* --- ADICIONE AO SEU STYLE SCOPED --- */

  @media (max-width: 1024px) {
    .testimonials-section {
      margin: 20px;
      padding: 60px 20px;
    }
    
    .testimonial-wrapper {
      min-width: 45%; /* Em tablets, permite dois cards lado a lado se houver espaço */
      max-width: 100%;
    }
  }

  @media (max-width: 768px) {
    .section-title {
      font-size: 1.8rem;
      margin-bottom: 40px;
    }

    .testimonials-grid {
      gap: 60px; /* Aumentamos o gap porque a foto vaza e precisa de espaço */
    }

    .testimonial-wrapper {
      flex: none;
      width: 100%;
      min-width: unset; /* Removemos a trava de 450px para não quebrar o layout */
      max-width: 100%;
    }

    .testimonial-card {
      min-width: unset; /* Removemos a trava de largura */
      min-height: auto; /* No mobile, deixamos o card crescer conforme o texto */
      padding: 30px 20px 60px 20px; /* Ajustamos os paddings */
    }

    .testimonial-text {
      font-size: 1rem;
      margin: 15px 0;
    }

    .quote-mark {
      font-size: 3.5rem; /* Aspas menores no mobile */
    }

    .client-photo {
      width: 70px;
      height: 70px;
    }

    .client-info {
      margin-top: -35px; /* Ajuste proporcional do vazamento da foto */
    }
  }

  @media (max-width: 480px) {
    .testimonials-section {
      border-radius: 20px; /* Arredondamento menos agressivo em telas pequenas */
      padding: 40px 15px;
    }

    .section-title {
      font-size: 1.5rem;
    }
  }

</style>